import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { WorkflowService } from '../../../../../../../../shared/services/workflow.service';
import { Subject } from 'rxjs';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import {
  MAT_DIALOG_DATA as MAT_DIALOG_DATA,
  MatDialog as MatDialog,
  MatDialogRef as MatDialogRef,
} from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { ProjectsService } from '../../../../../../core/projects.service';
import { DownloadReportRequest, FetchReportRequest } from '../../../../../../core/projects.actions';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import { AddReasonOrSignatureComponent } from '../add-reason-or-signature/add-reason-or-signature.component';
import { changeInWorkRequest, reportInfo } from '../../../../../../core/projects.selectors';
import { environment } from '../../../../../../../../../environments/environment';
import { PdfRenderService } from '../../../../../../../../shared/services/pdf-render.service';

@Component({
    selector: 'app-change-in-work-request-report',
    templateUrl: './change-in-work-request-report.component.html',
    styleUrls: ['./change-in-work-request-report.component.scss'],
    standalone: false
})
export class ChangeInWorkRequestReportComponent implements OnInit, OnDestroy {
  reportHtml: any;
  reportTitle: any;
  public reportWidth = 1120;
  public reportHeight = 1362;
  projectId: any;
  nextActions: any[];
  id: any;
  isDownloading: boolean;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private projectsService: ProjectsService,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private store: Store<fromRoot.State>,
    private workflowService: WorkflowService,
    public dialogRef: MatDialogRef<ChangeInWorkRequestReportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private pdfRenderService: PdfRenderService,
  ) {

  }

  ngOnInit() {
    if (this.data?.id) {
      this.id = this.data?.id;
    }

    this.commonService.closeDialog
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dialogRef.close();
      });

    this.projectsService.stopLoading
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.isDownloading = false;
      });

    this.projectsService.closeReportPopup
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        if (data) {
          this.close();
        }
      });

    this.store.select(reportInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.reportTitle = data?.title;
        this.reportHtml = data?.html;
        this.updateReportPreview(this.reportHtml);
      });

    this.store.select(changeInWorkRequest)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.projectId = data?.project?.id;
        this.nextActions = this.workflowService.canExecute() ? [...(data?.['next-actions'] ?? [])] : [];
        this.cdRef.detectChanges();
      });

    this.store.dispatch(FetchReportRequest({
      payload: {
        url: `${environment.API_ENDPOINT}${this.data?.reportUrl}`,
      },
    }));
  }

  close() {
    this.dialogRef.close();
  }

  updateReportPreview(html) {
    if (!html) {
      return;
    }

    // Override pagesize from css @page tag.
    const regex = /@page\s*{\s*size:\s*(\d+)px\s*(\d+)px;/gi;
    const found = regex.exec(html);

    if (found && found.length > 2) {
      this.reportWidth = parseInt(found[1], 10);
      this.reportHeight = parseInt(found[2], 10);
    }

    try {
      setTimeout(() => {
        const iframe = document.getElementById('iframe');
        if (iframe) {
          try {
            const contentWindow = iframe['contentWindow'];
            if (contentWindow && contentWindow.document) {
              const doc = contentWindow.document;
              doc.open();
              doc.write(html);
              doc.close();

              this.renderPdfAttachmentsAsCanvas(doc, contentWindow);
            }
          } catch (innerError) {
            console.error('Error accessing iframe content:', innerError);
          }
        }
      }, 100);
    } catch (e) {
      console.error('Error updating report preview:', e);
    }
  }

  private renderPdfAttachmentsAsCanvas(doc: Document, contentWindow: any) {
    const pdfElements = doc.querySelectorAll(
      'iframe[src*=".pdf"], object[data*=".pdf"], embed[src*=".pdf"]',
    );
    if (pdfElements.length === 0) return;

    pdfElements.forEach(async (el: any) => {
      const url = el.src || el.data || el.getAttribute('src');
      if (!url) return;

      const container = doc.createElement('div');
      container.style.cssText = 'width: 100%; overflow: hidden;';
      el.parentNode.replaceChild(container, el);

      try {
        const pdf = await this.pdfRenderService.loadDocument(url);
        for (let i = 1; i <= pdf.numPages; i++) {
          // Render in the main document — pdf.js registers the PDF's embedded
          // fonts there via @font-face, and a canvas owned by the iframe's
          // document can't see them (every glyph draws as a missing-glyph box).
          // Transfer the finished pixels into the iframe as an image instead.
          const dataUrl = await this.pdfRenderService.renderPageAsDataUrl(pdf, i, 1.5);
          const img = doc.createElement('img');
          img.src = dataUrl;
          img.style.cssText = 'width: 100%; height: auto; display: block; margin-bottom: 2px; background: white;';
          container.appendChild(img);
        }
      } catch (err) {
        console.error('Failed to render PDF:', err);
        const link = doc.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.textContent = 'Click to open PDF';
        link.style.cssText = 'display: block; padding: 10px; color: #1036d6;';
        container.appendChild(link);
      }
    });
  }

  executeState(action) {
    this.dialog.open(AddReasonOrSignatureComponent, {
      disableClose: true,
      width: '460px',
      height: '445px',
      data: {
        action,
        id: this.id,
        projectId: this.projectId,
        entity: this.data,
      },
    });
  }

  download() {
    this.isDownloading = !this.isDownloading;
    const payload = {
      reportTitle: this.reportTitle,
      url: `${environment.API_ENDPOINT}${this.data?.reportUrl}&download=true`,
    };
    this.store.dispatch(DownloadReportRequest({ payload }));
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

}
